/**
 * Wall-clock time in an IANA zone to the UTC instant it names, with daylight-saving
 * edge cases reported instead of resolved silently.
 *
 * On the night clocks go forward, some wall-clock times never happen (02:30 on
 * 8 March 2026 in New York); on the night they go back, some happen twice (01:30 on
 * 1 November 2026). The Date API carries no zone, so both are found by formatting
 * candidate instants back through Intl.DateTimeFormat and comparing.
 */

export interface WallTime {
  day: number;
  hour: number;
  minute: number;
  month: number;
  year: number;
}

/** "gap": the time is skipped; "overlap": it happens twice; "ok": it happens once. */
export type WallTimeKind = "ok" | "gap" | "overlap";

const MS_PER_MINUTE = 60_000;
/** Far enough to see both sides of any one transition, near enough to see only one. */
const PROBE_MS = 36 * 3_600_000;

/** Read the wall-clock time that a UTC instant shows in a given zone. */
export function getWallTime(date: Date, timeZone: string): WallTime {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const get = (type: string) =>
    Number.parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
  };
}

function wallAsUtcMs(wall: WallTime): number {
  const date = new Date(0);
  date.setUTCFullYear(wall.year, wall.month - 1, wall.day);
  date.setUTCHours(wall.hour, wall.minute, 0, 0);
  return date.getTime();
}

/** Milliseconds the zone's wall clock runs ahead of UTC at an instant. */
function offsetMsAt(ms: number, timeZone: string): number {
  return wallAsUtcMs(getWallTime(new Date(ms), timeZone)) - ms;
}

export interface ResolvedWallTime {
  instant: Date;
  kind: WallTimeKind;
}

/**
 * The instant a zone's wall-clock time names. A skipped time is moved forward by the
 * length of the jump (the offset in force before the transition is applied), and a
 * repeated time resolves to its first occurrence; `kind` says which happened.
 */
export function resolveWallTime(
  wall: WallTime,
  timeZone: string
): ResolvedWallTime {
  const target = wallAsUtcMs(wall);
  const before = offsetMsAt(target - PROBE_MS, timeZone);
  const after = offsetMsAt(target + PROBE_MS, timeZone);
  const valid: number[] = [];
  for (const offset of before === after ? [before] : [before, after]) {
    const candidate = target - offset;
    if (Math.abs(offsetMsAt(candidate, timeZone) - offset) < MS_PER_MINUTE) {
      valid.push(candidate);
    }
  }
  if (valid.length === 0) {
    return { instant: new Date(target - before), kind: "gap" };
  }
  return {
    instant: new Date(Math.min(...valid)),
    kind: valid.length > 1 ? "overlap" : "ok",
  };
}

/** Convert a wall-clock time in a source zone to the UTC instant it names. */
export function wallTimeToUtc(wall: WallTime, timeZone: string): Date {
  return resolveWallTime(wall, timeZone).instant;
}
